import React, { useRef, useState, useMemo, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Divider,
  FormControlLabel,
  Switch,
  Chip,
  useTheme,
  TextField,
  Button,
  Paper,
  Link,
  ToggleButton,
  ToggleButtonGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Snackbar,
  Alert,
  InputAdornment,
  Stack,
} from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import BedIcon from '@mui/icons-material/Bed';
import PersonIcon from '@mui/icons-material/Person';
import AddIcon from '@mui/icons-material/Add';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ImageIcon from '@mui/icons-material/Image';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import CloseIcon from '@mui/icons-material/Close';
import PhoneIcon from '@mui/icons-material/Phone';
import moment from 'moment';
import { alpha } from '@mui/material/styles';
import { HotelDateTimePicker } from '../../shared/components/forms/HotelDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import PaymentLinkDrawerOverlay from '../booking/PaymentLinkDrawerOverlay';
import { PAYMENT_LINK_PREFLIGHT_MSG } from '../booking/PaymentLinkSendPanel';
import {
  filterPaymentMethodsForComm,
  PAYMENT_LINK_METHOD,
  useBookingPaymentLinkComm,
} from '../../hooks/useBookingPaymentLinkComm';
import { PAYMENT_METHOD_CHIPS } from '../../utils/paymentMethodUtils';
import {
  REVIEW_ACCENT,
  accentGradient135,
  getReviewPalette,
  reviewCompactToggleGroupSx,
  reviewExtraChargesLinkSx,
  reviewGuestChipSx,
  reviewIconBadgeSx,
  reviewInvoiceHeaderCompactSx,
  reviewPageTitleSx,
  reviewPaymentMethodChipSx,
  reviewPaymentPanelSx,
  reviewSectionCardSx,
  reviewSectionHeadingSx,
  reviewSwitchSx,
  reviewTableShellSx,
  reviewTotalHighlightSx,
  reviewTotalsPanelSx,
  reviewTotalsRowSx,
} from './reviewStepStyles';
import useMobileView from '../../hooks/useMobileView';
import { buildRoomInvoiceLines } from './reviewInvoiceRoomLines';
import {
  createBookingDrawerOverlayProps,
  getBookingDrawerTheme,
  QUICK_BOOKING_DRAWER_WIDTH_SX,
} from '../../utils/bookingDrawerLayout';
import {
  ReviewInvoiceRoomDesktopRow,
  ReviewInvoiceRoomMobileCard,
  ReviewInvoiceRoomTableHeader,
} from './ReviewInvoiceRoomTable';
import ReviewTaxSection from './ReviewTaxSection';
import BookingExtraChargesOverlay from '../booking/BookingExtraChargesOverlay';
import BookingExtraChargesBreakdown from '../booking/BookingExtraChargesBreakdown';
import { getBookingCurrencySymbol } from '../../utils/bookingPaymentSummary';

const toCurrencyAmount = (value) => Math.round((Number(value) || 0) * 100) / 100;
const getSelectedHotelCurrency = () =>
  (typeof window !== 'undefined' && window.DJANGO_CONTEXT?.selectedHotel?.currency) || 'INR';

const sanitizeDecimalInput = (raw) => {
  let s = String(raw ?? '').replace(/[^\d.]/g, '');
  const dotIndex = s.indexOf('.');
  if (dotIndex !== -1) {
    s = `${s.slice(0, dotIndex + 1)}${s.slice(dotIndex + 1).replace(/\./g, '')}`;
  }
  return s;
};

const parseDecimalInput = (raw) => {
  const trimmed = String(raw ?? '').trim();
  if (trimmed === '' || trimmed === '.') return null;
  const n = parseFloat(trimmed);
  return Number.isFinite(n) ? n : null;
};

const PartialPaymentAmountField = ({
  value,
  onChange,
  disabled,
  maxAmount,
  currencySymbol = '₹',
  label = 'Payment Amount',
  sx,
}) => {
  const [draft, setDraft] = useState('');
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (!focused) {
      const amount = toCurrencyAmount(value);
      setDraft(amount > 0 ? String(amount) : '');
    }
  }, [value, focused]);

  const handleFocus = () => {
    setFocused(true);
    const amount = toCurrencyAmount(value);
    setDraft(amount > 0 ? String(amount) : '');
  };

  const handleChange = (e) => {
    setDraft(sanitizeDecimalInput(e.target.value));
  };

  const handleBlur = () => {
    setFocused(false);
    const max = toCurrencyAmount(maxAmount);
    let parsed = parseDecimalInput(draft);
    if (parsed == null) {
      parsed = 0;
    }
    parsed = toCurrencyAmount(Math.min(Math.max(parsed, 0), max));
    onChange(parsed);
    setDraft(parsed > 0 ? String(parsed) : '');
  };

  const displayValue = focused
    ? draft
    : (() => {
        const amount = toCurrencyAmount(value);
        return amount > 0 ? amount.toFixed(2) : '';
      })();

  return (
    <TextField
      fullWidth
      label={label}
      type="text"
      inputMode="decimal"
      disabled={disabled}
      value={disabled ? toCurrencyAmount(value).toFixed(2) : displayValue}
      onFocus={disabled ? undefined : handleFocus}
      onChange={disabled ? undefined : handleChange}
      onBlur={disabled ? undefined : handleBlur}
      placeholder={disabled ? undefined : '0.00'}
      helperText={
        !disabled && maxAmount > 0
          ? `Up to ${currencySymbol}${toCurrencyAmount(maxAmount).toFixed(2)}`
          : undefined
      }
      InputProps={{
        startAdornment: <InputAdornment position="start">{currencySymbol}</InputAdornment>,
      }}
      inputProps={{ inputMode: 'decimal' }}
      sx={{ mb: 2, ...sx }}
    />
  );
};

const collectionDateTimeFieldSx = (isDark) => ({
  mb: 2,
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'transparent !important',
    '& fieldset': {
      borderColor: '#4ce1b6',
    },
    '&:hover fieldset': {
      borderColor: '#4ce1b6',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#4ce1b6',
    },
  },
  '& .MuiInputBase-input': {
    backgroundColor: 'transparent !important',
    color: isDark ? '#ddd' : '#333',
  },
  '& .MuiInputLabel-root': {
    color: isDark ? '#999' : '#666',
  },
});

function CollectionDateTimeField({ value, onChange, isDark, datePickerSlotProps }) {
  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <HotelDateTimePicker
        label="Collection Date & Time"
        value={value}
        onChange={onChange}
        slotProps={{
          ...datePickerSlotProps,
          textField: {
            fullWidth: true,
            sx: collectionDateTimeFieldSx(isDark),
          },
        }}
      />
    </LocalizationProvider>
  );
}

const ReviewStep = ({
  checkInDate,
  checkOutDate,
  nights,
  rooms,
  guestName,
  guestEmail,
  guestPhone,
  guestAddress,
  bookingType,
  gstIncluded,
  taxInclusive = false,
  allowDiscount,
  discountMode = 'percentage',
  discountPercentage,
  discountAmount = 0,
  maxDiscountAmount = 0,
  autoGstEnabled = false,
  onGstIncludedChange,
  onTaxInclusiveChange = () => {},
  onAllowDiscountChange,
  onDiscountModeChange = () => {},
  onDiscountPercentageChange,
  onDiscountAmountChange = () => {},
  allowExtraCharges = false,
  extraChargesAmount = 0,
  extraChargesNote = '',
  extraChargesItems = [],
  extraChargesBaseAmount = 0,
  extraChargesGstAmount = 0,
  onAllowExtraChargesChange = () => {},
  onExtraChargesAmountChange = () => {},
  onExtraChargesNoteChange = () => {},
  onExtraChargesItemsChange = () => {},
  onExtraChargesBaseAmountChange = () => {},
  onExtraChargesGstAmountChange = () => {},
  priceBreakdown,
  // Reservation mode props
  collectAdvance,
  advanceAmount,
  collectionDate,
  paymentMethod,
  referenceNumber,
  advanceDocument,
  onCollectAdvanceChange,
  onAdvanceAmountChange,
  onCollectionDateChange,
  onPaymentMethodChange,
  onReferenceNumberChange,
  onAdvanceDocumentChange,
  // Walk-in mode props
  paymentType,
  paymentAmount,
  onPaymentTypeChange,
  onPaymentAmountChange,
  paymentDocument,
  onPaymentDocumentChange,
  isWalkIn = false,
  hasTaxRules = false,
  /** Quick-booking drawer: compact card chrome (same theme, tighter spacing) */
  flatReviewChrome = false,
  /** Render date/select overlays inside drawer (mobile z-index fix) */
  inDrawer = false,
  /** No payment-link modal on method pick; use booking-confirmed dialog instead. */
  suppressPaymentLinkOverlay = false,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { isCompact } = useMobileView();
  const drawerTheme = getBookingDrawerTheme(isDark);
  const drawerOverlaySurface = isDark ? drawerTheme.surface : '#ffffff';
  const drawerOverlays = useMemo(
    () => (inDrawer ? createBookingDrawerOverlayProps(isDark, drawerOverlaySurface) : null),
    [inDrawer, isDark, drawerOverlaySurface]
  );
  const skipPaymentLinkOverlay = Boolean(flatReviewChrome) || Boolean(suppressPaymentLinkOverlay);
  const reviewCurrency = getSelectedHotelCurrency();
  const reviewCurrencySymbol = getBookingCurrencySymbol(reviewCurrency);
  const formatCurrency = (value) => {
    return `${reviewCurrencySymbol}${toCurrencyAmount(value).toFixed(2)}`;
  };
  const fileInputRef = useRef(null);
  const paymentDocumentRef = useRef(null);
  const paymentDocumentVideoRef = useRef(null);
  const paymentDocumentCanvasRef = useRef(null);
  const [paymentCameraOpen, setPaymentCameraOpen] = useState(false);
  const [paymentCameraUpload, setPaymentCameraUpload] = useState(false);
  const [paymentStream, setPaymentStream] = useState(null);
  
  // Reservation payment camera refs
  const advanceDocumentVideoRef = useRef(null);
  const advanceDocumentCanvasRef = useRef(null);
  const [advanceCameraOpen, setAdvanceCameraOpen] = useState(false);
  const [advanceCameraUpload, setAdvanceCameraUpload] = useState(false);
  const [advanceStream, setAdvanceStream] = useState(null);
  const [cameraPermissionSnackbar, setCameraPermissionSnackbar] = useState({ open: false, message: '' });
  const [paymentLinkOverlayOpen, setPaymentLinkOverlayOpen] = useState(false);
  const [extraChargesOpen, setExtraChargesOpen] = useState(false);

  const showCameraPermissionMessage = (message) => {
    setCameraPermissionSnackbar({
      open: true,
      message:
        message ||
        'Camera access was denied or is unavailable. Allow camera permission in your browser settings, or turn off Camera upload to choose a file.',
    });
  };

  const flat = Boolean(flatReviewChrome);
  const reviewPalette = getReviewPalette(isDark);
  const lineSubtle = reviewPalette.lineSubtle;
  const CUSTOM_BORDER_COLOR = reviewPalette.border;

  const {
    canSendPaymentLink,
    canSendPaymentLinkEmail,
    canSendPaymentLinkWhatsApp,
  } = useBookingPaymentLinkComm();
  const paymentMethods = useMemo(
    () => filterPaymentMethodsForComm(PAYMENT_METHOD_CHIPS, canSendPaymentLink),
    [canSendPaymentLink]
  );

  const [fallbackCollectionDate, setFallbackCollectionDate] = useState(() => moment());
  const resolvedCollectionDate = collectionDate ?? fallbackCollectionDate;
  const handleCollectionDateChange = onCollectionDateChange ?? setFallbackCollectionDate;

  const bookingForPaymentLink = useMemo(
    () => ({
      guest_name: guestName || '',
      guest_email: (guestEmail || '').trim(),
      guest_phone: (guestPhone || '').trim(),
      guest: {
        email: (guestEmail || '').trim(),
        phone: (guestPhone || '').trim().replace(/\D/g, ''),
        first_name: (guestName || '').trim().split(/\s+/)[0] || '',
        last_name: (guestName || '').trim().split(/\s+/).slice(1).join(' ').trim() || '',
      },
    }),
    [guestName, guestEmail, guestPhone]
  );

  const paymentLinkAmount = useMemo(() => {
    if (isWalkIn) return toCurrencyAmount(paymentAmount);
    if (collectAdvance && (advanceAmount || 0) > 0) return toCurrencyAmount(advanceAmount);
    return toCurrencyAmount(priceBreakdown?.total);
  }, [isWalkIn, paymentAmount, collectAdvance, advanceAmount, priceBreakdown?.total]);

  const roomInvoiceLines = useMemo(
    () =>
      buildRoomInvoiceLines(rooms, {
        checkInDate,
        checkOutDate,
        nights,
        isWalkIn,
        bookingType,
        priceBreakdown,
        gstIncluded,
        taxInclusive,
        hasTaxRules,
      }),
    [
      rooms,
      checkInDate,
      checkOutDate,
      nights,
      isWalkIn,
      bookingType,
      priceBreakdown,
      gstIncluded,
      taxInclusive,
      hasTaxRules,
    ]
  );

  const handlePickPaymentMethod = useCallback(
    (method) => {
      const next = paymentMethod === method ? '' : method;
      onPaymentMethodChange(next);
      if (!skipPaymentLinkOverlay && canSendPaymentLink) {
        if (next === PAYMENT_LINK_METHOD) setPaymentLinkOverlayOpen(true);
        else setPaymentLinkOverlayOpen(false);
      }
    },
    [skipPaymentLinkOverlay, canSendPaymentLink, paymentMethod, onPaymentMethodChange]
  );

  useEffect(() => {
    if (skipPaymentLinkOverlay || !canSendPaymentLink || paymentMethod !== PAYMENT_LINK_METHOD) {
      setPaymentLinkOverlayOpen(false);
    }
  }, [skipPaymentLinkOverlay, canSendPaymentLink, paymentMethod]);

  useEffect(() => {
    if (!canSendPaymentLink && paymentMethod === PAYMENT_LINK_METHOD) {
      onPaymentMethodChange('');
    }
  }, [canSendPaymentLink, paymentMethod, onPaymentMethodChange]);

  // Set default payment type to 'full' for walk-in on mount
  React.useEffect(() => {
    if (isWalkIn && !paymentType && priceBreakdown?.total) {
      onPaymentTypeChange('full');
      const fullAmount = toCurrencyAmount(priceBreakdown.total);
      onPaymentAmountChange(fullAmount);
    }
  }, [isWalkIn, priceBreakdown?.total]);

  // Set default payment type to 'full' and payment method to 'CASH' for reservation on mount
  React.useEffect(() => {
    if (!isWalkIn && priceBreakdown?.total) {
      // Only set defaults if collectAdvance is false and advanceAmount is 0
      if (!collectAdvance && advanceAmount === 0) {
        setReservationPaymentType('full');
        onCollectAdvanceChange(true);
        const fullAmount = toCurrencyAmount(priceBreakdown.total);
        onAdvanceAmountChange(fullAmount);
      }
      // Set default payment method to CASH if not set
      if (!paymentMethod) {
        onPaymentMethodChange('CASH');
      }
    }
  }, [isWalkIn, priceBreakdown?.total]);

  // Keep full-payment amount in sync when total changes; preserve user-entered partial amounts.
  React.useEffect(() => {
    if (isWalkIn && priceBreakdown?.total) {
      if (paymentType === 'full') {
        const fullAmount = toCurrencyAmount(priceBreakdown.total);
        onPaymentAmountChange(fullAmount);
      }
      // For 'partial', keep the user's amount; for 'due', keep amount at 0
    }
  }, [
    isWalkIn,
    paymentType,
    priceBreakdown?.total,
    gstIncluded,
    taxInclusive,
    hasTaxRules,
    allowDiscount,
    discountMode,
    discountPercentage,
    discountAmount,
    allowExtraCharges,
    extraChargesAmount,
  ]);

  // Track payment type for reservation to update amount correctly
  const [reservationPaymentType, setReservationPaymentType] = React.useState('full');
  const isWalkInFullPayment = isWalkIn && paymentType === 'full';
  const isReservationFullPayment = !isWalkIn && reservationPaymentType === 'full';

  // Keep full advance in sync when total changes; preserve user-entered partial amounts.
  React.useEffect(() => {
    if (!isWalkIn && collectAdvance && priceBreakdown?.total) {
      if (reservationPaymentType === 'full') {
        const fullAmount = toCurrencyAmount(priceBreakdown.total);
        onAdvanceAmountChange(fullAmount);
      }
      // For 'partial', keep the user's amount; for 'due', amount stays at 0
    }
  }, [isWalkIn, collectAdvance, priceBreakdown?.total, gstIncluded, taxInclusive, allowDiscount, discountMode, discountPercentage, discountAmount, reservationPaymentType]);

  const startPaymentCamera = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      showCameraPermissionMessage(
        'Camera is not available. Turn off Camera upload to upload a payment document from files.'
      );
      return;
    }
    try {
      if (paymentStream) {
        paymentStream.getTracks().forEach(track => track.stop());
      }
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } }
      });
      setPaymentStream(mediaStream);
      setPaymentCameraOpen(true);
      setTimeout(() => {
        if (paymentDocumentVideoRef.current) {
          paymentDocumentVideoRef.current.srcObject = mediaStream;
          paymentDocumentVideoRef.current.play().catch(() => {});
        }
      }, 100);
    } catch (error) {
      showCameraPermissionMessage();
    }
  };

  const stopPaymentCamera = () => {
    if (paymentStream) {
      paymentStream.getTracks().forEach(track => track.stop());
      setPaymentStream(null);
    }
    setPaymentCameraOpen(false);
  };

  const capturePaymentDocument = () => {
    if (paymentDocumentVideoRef.current && paymentDocumentCanvasRef.current) {
      const video = paymentDocumentVideoRef.current;
      const canvas = paymentDocumentCanvasRef.current;
      const context = canvas.getContext('2d');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      context.drawImage(video, 0, 0);
      const imageData = canvas.toDataURL('image/jpeg', 0.8);
      onPaymentDocumentChange(imageData);
      stopPaymentCamera();
    }
  };

  const handlePaymentDocumentUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (event) => {
          onPaymentDocumentChange(event.target.result);
        };
        reader.readAsDataURL(file);
      }
    }
  };

  const handlePaymentDocumentClick = () => {
    if (paymentCameraUpload) {
      startPaymentCamera();
    } else {
      paymentDocumentRef.current?.click();
    }
  };

  // Cleanup camera stream on unmount
  React.useEffect(() => {
    return () => {
      if (paymentStream) {
        paymentStream.getTracks().forEach(track => track.stop());
      }
      if (advanceStream) {
        advanceStream.getTracks().forEach(track => track.stop());
      }
    };
  }, [paymentStream, advanceStream]);
  
  // Reservation payment camera functions
  const startAdvanceCamera = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      showCameraPermissionMessage(
        'Camera is not available. Turn off Camera upload to upload an advance document from files.'
      );
      return;
    }
    try {
      if (advanceStream) {
        advanceStream.getTracks().forEach(track => track.stop());
      }
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } }
      });
      setAdvanceStream(mediaStream);
      setAdvanceCameraOpen(true);
      setTimeout(() => {
        if (advanceDocumentVideoRef.current) {
          advanceDocumentVideoRef.current.srcObject = mediaStream;
          advanceDocumentVideoRef.current.play().catch(() => {});
        }
      }, 100);
    } catch (error) {
      showCameraPermissionMessage();
    }
  };

  const stopAdvanceCamera = () => {
    if (advanceStream) {
      advanceStream.getTracks().forEach(track => track.stop());
      setAdvanceStream(null);
    }
    setAdvanceCameraOpen(false);
  };

  const captureAdvanceDocument = () => {
    if (advanceDocumentVideoRef.current && advanceDocumentCanvasRef.current) {
      const video = advanceDocumentVideoRef.current;
      const canvas = advanceDocumentCanvasRef.current;
      const context = canvas.getContext('2d');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      context.drawImage(video, 0, 0);
      const imageData = canvas.toDataURL('image/jpeg', 0.8);
      onAdvanceDocumentChange(imageData);
      stopAdvanceCamera();
    }
  };
  
  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        onAdvanceDocumentChange(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };
  
  const handleBrowseClick = () => {
    if (advanceCameraUpload) {
      startAdvanceCamera();
    } else {
    fileInputRef.current?.click();
    }
  };

  return (
    <>
    <Box sx={{ position: 'relative', width: '100%' }}>
      {!flat && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" sx={reviewPageTitleSx(isDark)}>
            Review Your Reservation
          </Typography>
          <Box
            sx={{
              mt: 1.25,
              width: 56,
              height: 4,
              borderRadius: 2,
              background: accentGradient135,
              boxShadow: `0 2px 8px ${alpha(REVIEW_ACCENT, 0.35)}`,
            }}
          />
        </Box>
      )}

      <Grid container spacing={flat ? 2 : 3}>
        {/* Tax & discount */}
        <Grid item xs={12}>
          <Box sx={reviewSectionCardSx(isDark, flat)}>
            <ReviewTaxSection
              hasTaxRules={hasTaxRules}
              gstIncluded={gstIncluded}
              taxInclusive={taxInclusive}
              gstPercentage={priceBreakdown?.gstPercentage || 0}
              autoGstEnabled={autoGstEnabled}
              onGstIncludedChange={onGstIncludedChange}
              onTaxInclusiveChange={onTaxInclusiveChange}
              allowDiscount={allowDiscount}
              discountMode={discountMode}
              discountPercentage={discountPercentage}
              discountAmount={discountAmount}
              maxDiscountAmount={maxDiscountAmount || priceBreakdown?.priceBeforeDiscount || 0}
              onAllowDiscountChange={onAllowDiscountChange}
              onDiscountModeChange={onDiscountModeChange}
              onDiscountPercentageChange={onDiscountPercentageChange}
              onDiscountAmountChange={onDiscountAmountChange}
              isDark={isDark}
              currencySymbol={reviewCurrencySymbol}
              flat={flat}
            />

          </Box>
        </Grid>

        {/* Invoice */}
        <Grid item xs={12}>
          <Box
            sx={{
              ...reviewSectionCardSx(isDark, flat),
              p: 0,
            }}
          >
            <Box sx={reviewInvoiceHeaderCompactSx(isDark, flat)}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1.25}
                alignItems={{ xs: 'stretch', sm: 'center' }}
                justifyContent="space-between"
              >
                <Stack direction="row" alignItems="center" spacing={1.25} sx={{ minWidth: 0 }}>
                  <Box sx={reviewIconBadgeSx(true, isDark, flat ? 36 : 40)}>
                    <ReceiptLongOutlinedIcon />
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
                      <Typography
                        sx={{
                          color: isDark ? REVIEW_ACCENT : '#0f766e',
                          fontWeight: 800,
                          letterSpacing: '0.08em',
                          fontSize: flat ? '0.95rem' : '1.05rem',
                        }}
                      >
                        INVOICE
                      </Typography>
                      <Chip
                        label={bookingType === 'hourly' ? 'Hourly' : 'Nightly'}
                        size="small"
                        sx={{
                          height: 22,
                          fontWeight: 700,
                          fontSize: '0.68rem',
                          backgroundColor: isDark ? alpha('#fff', 0.08) : alpha(REVIEW_ACCENT, 0.14),
                          color: reviewPalette.text,
                          border: `1px solid ${alpha(REVIEW_ACCENT, 0.3)}`,
                        }}
                      />
                      {rooms.length > 0 ? (
                        <Chip
                          size="small"
                          icon={<BedIcon />}
                          label={`${rooms.length} room${rooms.length > 1 ? 's' : ''}`}
                          sx={reviewGuestChipSx(isDark)}
                        />
                      ) : null}
                    </Stack>
                  </Box>
                </Stack>
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap justifyContent={{ xs: 'flex-start', sm: 'flex-end' }}>
                  <Chip
                    size="small"
                    icon={<PersonIcon />}
                    label={guestName || 'Guest'}
                    sx={reviewGuestChipSx(isDark)}
                  />
                  <Chip
                    size="small"
                    icon={<PhoneIcon />}
                    label={guestPhone || '—'}
                    sx={reviewGuestChipSx(isDark)}
                  />
                </Stack>
              </Stack>
            </Box>

            <Box sx={{ p: flat ? 1.5 : { xs: 1.5, md: 2 } }}>
              {rooms.length > 0 && (
                <Box sx={{ ...reviewTableShellSx(isDark, flat), mb: 1.5 }}>
                  <ReviewInvoiceRoomTableHeader isDark={isDark} />
                  {isCompact
                    ? roomInvoiceLines.map((line) => (
                        <ReviewInvoiceRoomMobileCard
                          key={line.index}
                          line={line}
                          isDark={isDark}
                          flat={flat}
                          total={roomInvoiceLines.length}
                          formatCurrency={formatCurrency}
                          isWalkIn={isWalkIn}
                        />
                      ))
                    : roomInvoiceLines.map((line) => (
                        <ReviewInvoiceRoomDesktopRow
                          key={line.index}
                          line={line}
                          isDark={isDark}
                          flat={flat}
                          total={roomInvoiceLines.length}
                          formatCurrency={formatCurrency}
                          isWalkIn={isWalkIn}
                        />
                      ))}
                </Box>
              )}

              <Box sx={{ display: 'flex', justifyContent: { xs: 'stretch', md: 'flex-end' } }}>
                <Box sx={{ ...reviewTotalsPanelSx(isDark, flat), width: '100%', maxWidth: { md: 440 } }}>
                  {(() => {
                    const inclusiveDisplay = Boolean(priceBreakdown?.taxInclusive && gstIncluded && hasTaxRules);
                    const displayRoomBase = inclusiveDisplay
                      ? (priceBreakdown.netRoomBasePrice ?? priceBreakdown.roomBasePrice ?? 0)
                      : (priceBreakdown.roomBasePrice || 0);
                    const displayExtraAdult = inclusiveDisplay
                      ? (priceBreakdown.netExtraAdultCharge ?? priceBreakdown.totalExtraAdultCharge ?? 0)
                      : (priceBreakdown?.totalExtraAdultCharge || 0);
                    const displayExtraChild = inclusiveDisplay
                      ? (priceBreakdown.netExtraChildCharge ?? priceBreakdown.totalExtraChildCharge ?? 0)
                      : (priceBreakdown?.totalExtraChildCharge || 0);
                    const displaySubtotal = inclusiveDisplay
                      ? (priceBreakdown.netPriceBeforeDiscount ?? priceBreakdown.priceBeforeDiscount ?? 0)
                      : (priceBreakdown.priceBeforeDiscount || 0);
                    const displayAfterDiscount = inclusiveDisplay
                      ? (priceBreakdown.netAfterDiscount ?? priceBreakdown.priceAfterDiscount ?? 0)
                      : (priceBreakdown.priceAfterDiscount || 0);
                    const TotalsRow = ({ label, value, accent, valueColor }) => (
                      <Box sx={reviewTotalsRowSx(isDark, { accent })}>
                        <Typography className="review-totals-label">{label}</Typography>
                        <Typography className="review-totals-value" sx={valueColor ? { color: `${valueColor} !important` } : undefined}>
                          {value}
                        </Typography>
                      </Box>
                    );

                    return (
                      <>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            mb: 1,
                            pb: 0.75,
                            borderBottom: `1px solid ${lineSubtle}`,
                          }}
                        >
                          <Link
                            component="button"
                            type="button"
                            underline="hover"
                            onClick={() => {
                              onAllowExtraChargesChange(true);
                              setExtraChargesOpen(true);
                            }}
                            sx={reviewExtraChargesLinkSx(isDark)}
                          >
                            <AddIcon sx={{ fontSize: 16 }} />
                            {extraChargesAmount > 0 ? 'Edit Extra Charges' : 'Add Extra Charges'}
                          </Link>
                        </Box>

                        {allowExtraCharges && extraChargesAmount > 0 ? (
                          <Box sx={{ mb: 1 }}>
                            <BookingExtraChargesBreakdown
                              items={extraChargesItems}
                              amount={extraChargesAmount}
                              baseAmount={extraChargesBaseAmount || extraChargesAmount}
                              gstAmount={extraChargesGstAmount}
                              note={extraChargesNote}
                              currency={reviewCurrency}
                              isDark={isDark}
                              accent={REVIEW_ACCENT}
                              compact
                            />
                          </Box>
                        ) : null}

                        <TotalsRow label="Base Price" value={formatCurrency(displayRoomBase)} />
                        {displayExtraAdult > 0 ? (
                          <TotalsRow label="Extra Adult" value={formatCurrency(displayExtraAdult)} />
                        ) : null}
                        {displayExtraChild > 0 ? (
                          <TotalsRow label="Extra Child" value={formatCurrency(displayExtraChild)} />
                        ) : null}
                        <TotalsRow label="Subtotal" value={formatCurrency(displaySubtotal)} />
                        {allowDiscount && priceBreakdown.totalDiscount > 0 ? (
                          <>
                            <TotalsRow
                              label={`Discount (${priceBreakdown.discountDisplayLabel || `${discountPercentage}%`})`}
                              value={`- ${formatCurrency(priceBreakdown.totalDiscount)}`}
                              valueColor={theme.palette.error.main}
                            />
                            <TotalsRow
                              label={inclusiveDisplay ? 'Taxable Amount' : 'After Discount'}
                              value={formatCurrency(displayAfterDiscount)}
                            />
                          </>
                        ) : null}
                        {gstIncluded && hasTaxRules ? (
                          <TotalsRow
                            label={`GST${priceBreakdown?.gstPercentage ? ` (${priceBreakdown.gstPercentage}%)` : ''}${inclusiveDisplay ? ' · Incl.' : ''}`}
                            value={`${inclusiveDisplay ? '+ ' : ''}${formatCurrency(priceBreakdown.totalGST)}`}
                          />
                        ) : null}
                        {allowExtraCharges && priceBreakdown?.extraChargesAmount > 0 ? (
                          <TotalsRow
                            label="Extra Charges"
                            value={formatCurrency(priceBreakdown.extraChargesAmount)}
                            accent
                          />
                        ) : null}

                        <Box sx={{ ...reviewTotalHighlightSx(isDark, flat), mt: 1.25 }}>
                          <Typography sx={{ color: '#000', fontWeight: 800, fontSize: '0.85rem', letterSpacing: '0.04em' }}>
                            TOTAL
                          </Typography>
                          <Typography
                            sx={{
                              color: '#000',
                              fontWeight: 800,
                              fontSize: flat ? '1.2rem' : '1.35rem',
                              fontVariantNumeric: 'tabular-nums',
                            }}
                          >
                            {formatCurrency(priceBreakdown.total)}
                          </Typography>
                        </Box>
                      </>
                    );
                  })()}
                </Box>
              </Box>
            </Box>
          </Box>
        </Grid>

        <BookingExtraChargesOverlay
          open={extraChargesOpen}
          onClose={() => setExtraChargesOpen(false)}
          localMode
          currency={reviewCurrency}
          existingAmount={extraChargesAmount}
          existingBaseAmount={extraChargesBaseAmount || extraChargesAmount}
          existingGstAmount={extraChargesGstAmount}
          existingNote={extraChargesNote}
          existingItems={extraChargesItems}
          roomGstPercent={priceBreakdown?.gstPercentage || 0}
          isDark={isDark}
          accent={REVIEW_ACCENT}
          drawerWidthSx={flatReviewChrome ? QUICK_BOOKING_DRAWER_WIDTH_SX : undefined}
          onSaved={(result) => {
            const amount = Number(result?.extra_charges_amount) || 0;
            onExtraChargesAmountChange(amount);
            onExtraChargesBaseAmountChange(Number(result?.extra_charges_base_amount) || 0);
            onExtraChargesGstAmountChange(Number(result?.extra_charges_gst_amount) || 0);
            onExtraChargesNoteChange(result?.extra_charges_note || '');
            onExtraChargesItemsChange(
              Array.isArray(result?.extra_charges_items) ? result.extra_charges_items : [],
            );
            onAllowExtraChargesChange(amount > 0);
          }}
        />

        {/* Payment */}
        <Grid item xs={12}>
          <Box sx={reviewSectionCardSx(isDark, flat)}>
              {isWalkIn ? (
                <Box>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  spacing={1}
                  sx={{ mb: 1.5, pt: 0.25 }}
                  flexWrap="wrap"
                  useFlexGap
                >
                  <Stack direction="row" alignItems="center" spacing={1.25}>
                    <Box sx={reviewIconBadgeSx(true, isDark, flat ? 36 : 40)}>
                      <PaymentsOutlinedIcon />
                    </Box>
                    <Box>
                      <Typography sx={{ ...reviewSectionHeadingSx(isDark, flat), fontWeight: 800, letterSpacing: '0.03em' }}>
                        Payment
                      </Typography>
                      <Typography sx={{ color: reviewPalette.textMuted, fontSize: '0.72rem', mt: 0.15 }}>
                        Collect now or leave as due
                      </Typography>
                    </Box>
                  </Stack>
                  {priceBreakdown?.total > 0 ? (
                    <Chip
                      size="small"
                      label={`Due ${formatCurrency(priceBreakdown.total)}`}
                      sx={{
                        height: 26,
                        fontWeight: 700,
                        fontSize: '0.72rem',
                        background: accentGradient135,
                        color: '#000',
                      }}
                    />
                  ) : null}
                </Stack>
                  <ToggleButtonGroup
                  value={paymentType || 'full'}
                    exclusive
                    onChange={(e, newValue) => {
                      if (newValue !== null) {
                        onPaymentTypeChange(newValue);
                        // Auto-set payment amount based on type
                        if (newValue === 'full') {
                          const fullAmount = toCurrencyAmount(priceBreakdown.total);
                          onPaymentAmountChange(fullAmount);
                        } else if (newValue === 'partial') {
                          const partialAmount = toCurrencyAmount(priceBreakdown.total / 2);
                          onPaymentAmountChange(partialAmount);
                        } else {
                          onPaymentAmountChange(0);
                        }
                      }
                    }}
                    sx={{
                      ...reviewCompactToggleGroupSx(isDark, CUSTOM_BORDER_COLOR),
                      mb: 1.5,
                    }}
                  >
                    <ToggleButton value="full">Full</ToggleButton>
                    <ToggleButton value="partial">Partial</ToggleButton>
                    <ToggleButton value="due">Due</ToggleButton>
                  </ToggleButtonGroup>

                  {paymentType && paymentType !== 'due' && (
                    <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
                      {/* Left Side - Payment Details */}
                      <Grid item xs={12} md={6}>
                        <Box sx={reviewPaymentPanelSx(isDark, flat)}>
                          {/* Payment Amount - Half Width */}
                      <PartialPaymentAmountField
                        key={paymentType}
                        value={paymentAmount}
                        onChange={onPaymentAmountChange}
                        disabled={isWalkInFullPayment}
                        maxAmount={priceBreakdown?.total || 0}
                        currencySymbol={reviewCurrencySymbol}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                              borderColor: CUSTOM_BORDER_COLOR,
                            },
                            '&:hover fieldset': {
                              borderColor: '#4ce1b6',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#4ce1b6',
                            },
                          },
                        }}
                      />

                      <CollectionDateTimeField
                        value={resolvedCollectionDate}
                        onChange={handleCollectionDateChange}
                        isDark={isDark}
                        datePickerSlotProps={drawerOverlays?.datePickerSlotProps}
                      />

                      {/* Reference Number */}
                      <TextField
                        fullWidth
                            label="Reference Number"
                        value={referenceNumber || ''}
                        onChange={(e) => onReferenceNumberChange(e.target.value)}
                        placeholder="Enter Reference Number"
                        sx={{
                              mb: 2,
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                              borderColor: CUSTOM_BORDER_COLOR,
                            },
                            '&:hover fieldset': {
                                  borderColor: '#4ce1b6',
                            },
                            '&.Mui-focused fieldset': {
                                  borderColor: '#4ce1b6',
                            },
                          },
                        }}
                      />

                          {/* Payment Document Upload with Camera */}
                          <Box>
                            <Grid container spacing={1} sx={{ mb: 1, alignItems: 'center' }}>
                              <Grid item xs={6}>
                                <Typography variant="body2" sx={{ fontWeight: 500, color: isDark ? '#ddd' : 'rgb(88.3561643836, 91.0068493151, 105.1438356164)' }}>
                                  Payment Receipt
                                </Typography>
                              </Grid>
                              <Grid item xs={6} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <FormControlLabel
                    control={
                      <Switch
                                      checked={paymentCameraUpload}
                                      onChange={(e) => setPaymentCameraUpload(e.target.checked)}
                                      size="small"
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: '#4ce1b6',
                          },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: '#4ce1b6',
                          },
                        }}
                      />
                    }
                    label={
                                    <Typography variant="caption" sx={{ fontSize: '0.75rem', color: isDark ? '#999' : '#666' }}>
                                      Use Camera
              </Typography>
                    }
                                  sx={{ m: 0 }}
                                />
                              </Grid>
                            </Grid>
                            <Paper
                              sx={{
                                p: 1.5,
                                border: `2px dashed ${paymentDocument ? '#4ce1b6' : CUSTOM_BORDER_COLOR}`,
                                borderRadius: 2,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                minHeight: 88,
                                backgroundColor: isDark ? alpha('#fff', 0.03) : alpha(REVIEW_ACCENT, 0.03),
                                cursor: 'pointer',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                  borderColor: '#4ce1b6',
                                  backgroundColor: isDark ? alpha(REVIEW_ACCENT, 0.08) : alpha(REVIEW_ACCENT, 0.06),
                                  boxShadow: '0 4px 12px rgba(76, 225, 182, 0.2)',
                                },
                              }}
                              onClick={handlePaymentDocumentClick}
                            >
                              {paymentDocument ? (
                                <Box sx={{ textAlign: 'center', width: '100%', position: 'relative' }}>
                                  <img 
                                    src={paymentDocument} 
                                    alt="Payment Document" 
                                    style={{ 
                                      maxWidth: '100%', 
                                      maxHeight: '100px', 
                                      objectFit: 'contain',
                                      borderRadius: '4px'
                                    }} 
                                  />
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      onPaymentDocumentChange('');
                                    }}
                                    sx={{
                                      position: 'absolute',
                                      top: 0,
                                      right: 0,
                                      backgroundColor: 'rgba(244, 67, 54, 0.9)',
                                      color: '#fff',
                                      '&:hover': {
                                        backgroundColor: 'rgba(244, 67, 54, 1)',
                                      },
                                    }}
                                  >
                                    <CloseIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              ) : (
                                <Box sx={{ textAlign: 'center' }}>
                                  {paymentCameraUpload ? (
                                    <CameraAltIcon sx={{ fontSize: 32, color: isDark ? '#666' : '#999', mb: 0.5 }} />
                                  ) : (
                                    <ImageIcon sx={{ fontSize: 32, color: isDark ? '#666' : '#999', mb: 0.5 }} />
                                  )}
                                  <Typography variant="caption" sx={{ color: isDark ? '#999' : '#666', display: 'block', fontSize: '0.7rem' }}>
                                    {paymentCameraUpload ? 'Click to capture' : 'Click to upload'}
                                  </Typography>
                                </Box>
                              )}
                            </Paper>
                            <input
                              ref={paymentDocumentRef}
                              type="file"
                              accept="image/*"
                              style={{ display: 'none' }}
                              onChange={handlePaymentDocumentUpload}
                            />
                            <canvas ref={paymentDocumentCanvasRef} style={{ display: 'none' }} />
                            {paymentCameraOpen && (
                              <Dialog
                                open={paymentCameraOpen}
                                onClose={stopPaymentCamera}
                                maxWidth="sm"
                                fullWidth
                                PaperProps={{
                                  sx: {
                                    backgroundColor: isDark ? '#2a2a31' : '#ffffff',
                                    borderRadius: 2,
                                  },
                                }}
                              >
                                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <Typography variant="h6">Capture Payment Receipt</Typography>
                                  <IconButton onClick={stopPaymentCamera} size="small">
                                    <CloseIcon />
                                  </IconButton>
                                </DialogTitle>
                                <DialogContent>
                                  <Box sx={{ position: 'relative', width: '100%', backgroundColor: '#000', borderRadius: 1, overflow: 'hidden' }}>
                                    <video
                                      ref={paymentDocumentVideoRef}
                                      autoPlay
                                      playsInline
                                      style={{
                                        width: '100%',
                                        height: 'auto',
                                        display: 'block',
                                      }}
                                    />
                                  </Box>
                                </DialogContent>
                                <DialogActions sx={{ p: 2 }}>
                                  <Button onClick={stopPaymentCamera} variant="outlined">
                                    Cancel
                                  </Button>
                                  <Button
                                    onClick={capturePaymentDocument}
                                    variant="contained"
                                    startIcon={<PhotoCameraIcon />}
                                    sx={{
                                      backgroundColor: '#4ce1b6',
                                      color: '#000',
                                      '&:hover': {
                                        backgroundColor: '#3ab896',
                                      },
                                    }}
                                  >
                                    Capture
                                  </Button>
                                </DialogActions>
                              </Dialog>
                            )}
                          </Box>
                        </Box>
                      </Grid>

                      {/* Right Side - Payment Methods */}
                      <Grid item xs={12} md={6}>
                        <Box sx={reviewPaymentPanelSx(isDark, flat)}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                          <Typography variant="body2" sx={{ color: reviewPalette.textMuted, fontWeight: 700, fontSize: '0.78rem', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                            Method
                          </Typography>
                          <Typography variant="caption" sx={{ color: theme.palette.error.main, fontSize: '0.7rem' }}>
                            *
                          </Typography>
                        </Box>
                        {!paymentMethod && paymentType && paymentType !== 'due' && (
                          <Typography variant="caption" sx={{ color: theme.palette.error.main, mb: 1, display: 'block', fontSize: '0.7rem' }}>
                            Please select a payment method
                          </Typography>
                        )}
                        <Box sx={{ 
                          display: 'flex', 
                          flexWrap: 'wrap', 
                          gap: 0.75,
                        }}>
                          {paymentMethods.map((method) => (
                            <Chip
                              key={method}
                              label={method}
                              onClick={() => handlePickPaymentMethod(method)}
                              sx={reviewPaymentMethodChipSx(
                                isDark,
                                paymentMethod === method,
                                !paymentMethod && paymentType && paymentType !== 'due',
                              )}
                            />
                          ))}
                        </Box>
                        </Box>
                      </Grid>
                    </Grid>
                  )}
                </Box>
              ) : (
                <Box>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    spacing={1}
                    sx={{ mb: 1.5, pt: 0.25 }}
                    flexWrap="wrap"
                    useFlexGap
                  >
                    <Stack direction="row" alignItems="center" spacing={1.25}>
                      <Box sx={reviewIconBadgeSx(true, isDark, flat ? 36 : 40)}>
                        <PaymentsOutlinedIcon />
                      </Box>
                      <Box>
                        <Typography sx={{ ...reviewSectionHeadingSx(isDark, flat), fontWeight: 800, letterSpacing: '0.03em' }}>
                          Payment
                        </Typography>
                        <Typography sx={{ color: reviewPalette.textMuted, fontSize: '0.72rem', mt: 0.15 }}>
                          Advance now or confirm as due
                        </Typography>
                      </Box>
                    </Stack>
                    {priceBreakdown?.total > 0 ? (
                      <Chip
                        size="small"
                        label={`Booking ${formatCurrency(priceBreakdown.total)}`}
                        sx={{
                          height: 26,
                          fontWeight: 700,
                          fontSize: '0.72rem',
                          background: accentGradient135,
                          color: '#000',
                        }}
                      />
                    ) : null}
                  </Stack>

                  <ToggleButtonGroup
                    value={reservationPaymentType}
                    exclusive
                    onChange={(e, newValue) => {
                      if (newValue !== null) {
                        setReservationPaymentType(newValue);
                        onCollectAdvanceChange(newValue === 'partial' || newValue === 'full');
                        if (newValue === 'full') {
                          const fullAmount = toCurrencyAmount(priceBreakdown.total);
                          onAdvanceAmountChange(fullAmount);
                        } else if (newValue === 'partial') {
                          const partialAmount = toCurrencyAmount(priceBreakdown.total / 2);
                          onAdvanceAmountChange(partialAmount);
                        } else {
                          onAdvanceAmountChange(0);
                          onCollectAdvanceChange(false);
                        }
                      }
                    }}
                    sx={{
                      ...reviewCompactToggleGroupSx(isDark, CUSTOM_BORDER_COLOR),
                      mb: 1.5,
                    }}
                  >
                    <ToggleButton value="full">Full</ToggleButton>
                    <ToggleButton value="partial">Partial</ToggleButton>
                    <ToggleButton value="due">Due</ToggleButton>
                  </ToggleButtonGroup>

                  {(collectAdvance || (advanceAmount > 0)) && (
                    <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
                      {/* Left Side - Payment Details */}
                      <Grid item xs={12} md={6}>
                        <Box sx={reviewPaymentPanelSx(isDark, flat)}>
                          {/* Payment Amount - Half Width */}
                      <PartialPaymentAmountField
                        key={reservationPaymentType}
                        value={advanceAmount}
                        onChange={onAdvanceAmountChange}
                        disabled={isReservationFullPayment}
                        maxAmount={priceBreakdown?.total || 0}
                        currencySymbol={reviewCurrencySymbol}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: 'transparent !important',
                            '& fieldset': {
                              borderColor: CUSTOM_BORDER_COLOR,
                            },
                            '&:hover fieldset': {
                              borderColor: '#4ce1b6',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#4ce1b6',
                            },
                          },
                          '& .MuiInputBase-input': {
                            backgroundColor: 'transparent !important',
                            color: isDark ? '#ddd' : '#333',
                          },
                          '& .MuiInputLabel-root': {
                            color: isDark ? '#999' : '#666',
                          },
                        }}
                      />

                          {/* Collection Date & Time */}
                      <CollectionDateTimeField
                        value={resolvedCollectionDate}
                        onChange={handleCollectionDateChange}
                        isDark={isDark}
                        datePickerSlotProps={drawerOverlays?.datePickerSlotProps}
                      />
                    
                    {/* Reference Number */}
                      <TextField
                        fullWidth
                            label="Reference Number"
                            value={referenceNumber || ''}
                        onChange={(e) => onReferenceNumberChange(e.target.value)}
                        placeholder="Enter Reference Number"
                        sx={{
                              mb: 2,
                          '& .MuiOutlinedInput-root': {
                                backgroundColor: 'transparent !important',
                            '& fieldset': {
                                  borderColor: CUSTOM_BORDER_COLOR,
                            },
                            '&:hover fieldset': {
                              borderColor: '#4ce1b6',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#4ce1b6',
                            },
                          },
                              '& .MuiInputBase-input': {
                                backgroundColor: 'transparent !important',
                                color: isDark ? '#ddd' : '#333',
                              },
                              '& .MuiInputLabel-root': {
                                color: isDark ? '#999' : '#666',
                          },
              }}
            />

                          {/* Payment Document Upload with Camera */}
                          <Box>
                            <Grid container spacing={1} sx={{ mb: 1, alignItems: 'center' }}>
                              <Grid item xs={6}>
                                <Typography variant="body2" sx={{ fontWeight: 500, color: isDark ? '#ddd' : 'rgb(88.3561643836, 91.0068493151, 105.1438356164)' }}>
                                  Payment Receipt
                </Typography>
                              </Grid>
                              <Grid item xs={6} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                <FormControlLabel
                                  control={
                                    <Switch
                                      checked={advanceCameraUpload}
                                      onChange={(e) => setAdvanceCameraUpload(e.target.checked)}
                                      size="small"
                                      sx={{
                                        '& .MuiSwitch-switchBase.Mui-checked': {
                                          color: '#4ce1b6',
                                        },
                                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                          backgroundColor: '#4ce1b6',
                                        },
                                      }}
                                    />
                                  }
                                  label={
                                    <Typography variant="caption" sx={{ fontSize: '0.75rem', color: isDark ? '#999' : '#666' }}>
                                      Use Camera
                                    </Typography>
                                  }
                                  sx={{ m: 0 }}
                                />
                              </Grid>
                            </Grid>
                          <Paper
                            sx={{
                                p: 1.5,
                                border: `2px dashed ${advanceDocument ? '#4ce1b6' : CUSTOM_BORDER_COLOR}`,
                              borderRadius: 2,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                                minHeight: 88,
                              backgroundColor: isDark ? alpha('#fff', 0.03) : alpha(REVIEW_ACCENT, 0.03),
                              cursor: 'pointer',
                                transition: 'all 0.3s ease',
                              '&:hover': {
                                borderColor: '#4ce1b6',
                                backgroundColor: isDark ? alpha(REVIEW_ACCENT, 0.08) : alpha(REVIEW_ACCENT, 0.06),
                                  boxShadow: '0 4px 12px rgba(76, 225, 182, 0.2)',
                              },
                            }}
                            onClick={handleBrowseClick}
                          >
                            {advanceDocument ? (
                                <Box sx={{ textAlign: 'center', width: '100%', position: 'relative' }}>
                                <img 
                                  src={advanceDocument} 
                                    alt="Payment Document" 
                                  style={{ 
                                    maxWidth: '100%', 
                                      maxHeight: '100px', 
                                    objectFit: 'contain',
                                    borderRadius: '4px'
                                  }} 
                                />
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      onAdvanceDocumentChange('');
                                    }}
                                    sx={{
                                      position: 'absolute',
                                      top: 0,
                                      right: 0,
                                      backgroundColor: 'rgba(244, 67, 54, 0.9)',
                                      color: '#fff',
                                      '&:hover': {
                                        backgroundColor: 'rgba(244, 67, 54, 1)',
                                      },
                                    }}
                                  >
                                    <CloseIcon fontSize="small" />
                                  </IconButton>
              </Box>
                            ) : (
                              <Box sx={{ textAlign: 'center' }}>
                                  {paymentCameraUpload ? (
                                    <CameraAltIcon sx={{ fontSize: 32, color: isDark ? '#666' : '#999', mb: 0.5 }} />
                                  ) : (
                                    <ImageIcon sx={{ fontSize: 32, color: isDark ? '#666' : '#999', mb: 0.5 }} />
                                  )}
                                  <Typography variant="caption" sx={{ color: isDark ? '#999' : '#666', display: 'block', fontSize: '0.7rem' }}>
                                    {paymentCameraUpload ? 'Click to capture' : 'Click to upload'}
                    </Typography>
                  </Box>
                            )}
                          </Paper>
                          <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            style={{ display: 'none' }}
                            onChange={handleFileUpload}
                          />
                            <canvas ref={advanceDocumentCanvasRef} style={{ display: 'none' }} />
                            {advanceCameraOpen && (
                              <Dialog
                                open={advanceCameraOpen}
                                onClose={stopAdvanceCamera}
                                maxWidth="sm"
                                fullWidth
                                PaperProps={{
                                  sx: {
                                    backgroundColor: isDark ? '#2a2a31' : '#ffffff',
                                    borderRadius: 2,
                                  },
                                }}
                              >
                                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <Typography variant="h6">Capture Payment Receipt</Typography>
                                  <IconButton onClick={stopAdvanceCamera} size="small">
                                    <CloseIcon />
                                  </IconButton>
                                </DialogTitle>
                                <DialogContent>
                                  <Box sx={{ position: 'relative', width: '100%', backgroundColor: '#000', borderRadius: 1, overflow: 'hidden' }}>
                                    <video
                                      ref={advanceDocumentVideoRef}
                                      autoPlay
                                      playsInline
                                      style={{
                                        width: '100%',
                                        height: 'auto',
                                        display: 'block',
                                      }}
                                    />
                                  </Box>
                                </DialogContent>
                                <DialogActions sx={{ p: 2 }}>
                                  <Button onClick={stopAdvanceCamera} variant="outlined">
                                    Cancel
                                  </Button>
                          <Button
                                    onClick={captureAdvanceDocument}
                            variant="contained"
                                    startIcon={<PhotoCameraIcon />}
                            sx={{
                              backgroundColor: '#4ce1b6',
                                      color: '#000',
                              '&:hover': {
                                backgroundColor: '#3ab896',
                              },
                            }}
                          >
                                    Capture
                          </Button>
                                </DialogActions>
                              </Dialog>
                            )}
                          </Box>
                        </Box>
                        </Grid>

                      {/* Right Side - Payment Methods */}
                      <Grid item xs={12} md={6}>
                        <Box sx={reviewPaymentPanelSx(isDark, flat)}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                            <Typography variant="body2" sx={{ color: reviewPalette.textMuted, fontWeight: 700, fontSize: '0.78rem', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                              Method
                            </Typography>
                            <Typography variant="caption" sx={{ color: theme.palette.error.main, fontSize: '0.7rem' }}>
                              *
                            </Typography>
                          </Box>
                          {!paymentMethod && (collectAdvance || advanceAmount > 0) && (
                            <Typography variant="caption" sx={{ color: theme.palette.error.main, mb: 1, display: 'block', fontSize: '0.7rem' }}>
                              Please select a payment method
                            </Typography>
                          )}
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                            {paymentMethods.map((method) => (
                              <Chip
                                key={method}
                                label={method}
                                onClick={() => handlePickPaymentMethod(method)}
                                sx={reviewPaymentMethodChipSx(
                                  isDark,
                                  paymentMethod === method,
                                  !paymentMethod && (collectAdvance || advanceAmount > 0),
                                )}
                              />
                            ))}
                          </Box>
                        </Box>
                      </Grid>
                    </Grid>
                  )}
                </Box>
              )}
            </Box>
        </Grid>
      </Grid>

      {!skipPaymentLinkOverlay && canSendPaymentLink ? (
        <PaymentLinkDrawerOverlay
          open={paymentLinkOverlayOpen}
          onClose={() => setPaymentLinkOverlayOpen(false)}
          bookingId={0}
          booking={bookingForPaymentLink}
          amount={paymentLinkAmount}
          currency={reviewCurrency}
          isDark={isDark}
          accent="#4ce1b6"
          allowSend={false}
          sendBlockedMessage={PAYMENT_LINK_PREFLIGHT_MSG}
          canSendEmail={canSendPaymentLinkEmail}
          canSendWhatsApp={canSendPaymentLinkWhatsApp}
        />
      ) : null}
    </Box>

    <Snackbar
      open={cameraPermissionSnackbar.open}
      autoHideDuration={9000}
      onClose={() => setCameraPermissionSnackbar((s) => ({ ...s, open: false }))}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Alert
        onClose={() => setCameraPermissionSnackbar((s) => ({ ...s, open: false }))}
        severity="warning"
        variant="filled"
        sx={{ width: '100%', alignItems: 'center' }}
      >
        {cameraPermissionSnackbar.message}
      </Alert>
    </Snackbar>
    </>
  );
};

export default ReviewStep;

